<!--
	Countermeasures panel for a Prioritize (corridor) feature.
	Uses @safesystem/client-playbook-lib for intervention logic.
-->
<script lang="ts">
	import type { MapGeoJSONFeature } from 'maplibre-gl';
	import {
		ECorridorPriority,
		ECountermeasureCategory,
		type ETrafficVolume,
		getCorridorRecommendations
	} from '@safesystem/client-playbook-lib';
	import { mapPriorityDescription } from '../prioritize';
	import DataAttributes from './DataAttributes.svelte';
	import { aadtFromLabel } from './aadt';
	import CountermeasureCategories from './CountermeasureCategories.svelte';

	interface Props {
		feature: MapGeoJSONFeature;
	}

	const { feature }: Props = $props();

	const priority = $derived(
		ECorridorPriority[
			mapPriorityDescription[
				feature.properties.priority_rating as string
			] as keyof typeof ECorridorPriority
		]
	);
	const isAlignmentTortuous = $derived(feature.properties.alignment_category === 'Tortuous');
	const isPaved = $derived(feature.properties.road_seal === 1);

	// User can override the data's AADT band.
	let aadtBand = $derived(aadtFromLabel(feature.properties.aadt_band));

	const categories = Object.values(ECountermeasureCategory);

	const recommendations = $derived(
		getCorridorRecommendations({
			priority,
			tortuous: isAlignmentTortuous,
			aadtBand: aadtBand as ETrafficVolume,
			isPaved
		})
	);

	const delineation = $derived(recommendations.delineation);
	const maintenance = $derived(recommendations.maintenance);

	const hasCountermeasures = $derived(
		categories.some((cat) => delineation[cat].length + maintenance[cat].length > 0)
	);

	const sectionsFor = (category: ECountermeasureCategory) => [
		{
			heading: 'Delineation',
			items: delineation[category],
			signage:
				category === ECountermeasureCategory.Foundational ? recommendations.signage : undefined
		},
		{ heading: 'Maintenance', items: maintenance[category] }
	];
</script>

{#if !hasCountermeasures}
	<p class="noTreatment">Treat individual curves along the corridor as required.</p>
{:else}
	<div class="inputs">
		<DataAttributes summary="Modify data attributes for this corridor" bind:aadtBand />
	</div>

	<CountermeasureCategories {sectionsFor} subject="corridor" headingLevel={3} />
{/if}

<style>
	.noTreatment {
		padding: var(--space-5) var(--space-1);
	}

	.inputs {
		margin-bottom: var(--space-2);
		border-bottom: 2px solid var(--border-strong);
		padding: 0 var(--space-2) var(--space-4);
	}
</style>
